import React from 'react'
import { useIntl } from 'react-intl'
import {
  SkeletorTableState,
  Table
} from '@contrast-security-inc/design-system-tables'
import { ObservationType } from 'src/sca/libraries/api/react-query/libraryUsage/response-types'
import { SearchInput } from '@contrast-security-inc/design-system-inputs'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import { Text } from '@contrast-security-inc/design-system-typography'
import { LibraryAgentLanguage } from '../library/library-agent-language'
import { Library } from '../../helpers/ui-interfaces'
import { LibraryUsageGridEmptyState } from './grid-empty-state'
import { getApplicationLibraryClassUsageCount } from '../../helpers/get-application-library-class-usage-count'
import { useLibraryUsage } from '../../api/react-query/libraryUsage/library-usage'
import { useLibraryUsageGridContext } from './library-usage-context'
import { getColumns } from './columns'

// Determine if this is a true "no data" state, as opposed to a "no data matching filters state".
export const calculateDoesNotHaveDataInitially = ({
  collectionLoading,
  totalRowCount,
  filterTextInputHasBeenInteractedWith
}: {
  collectionLoading: boolean
  totalRowCount: number
  filterTextInputHasBeenInteractedWith: boolean
}) =>
  !collectionLoading &&
  totalRowCount === 0 &&
  !filterTextInputHasBeenInteractedWith

// Determine if this is an empty data state, regardless of why
export const calculateHasData = ({
  doesNotHaveDataInitially,
  totalRowCount
}: {
  doesNotHaveDataInitially: boolean
  totalRowCount: number
}) => doesNotHaveDataInitially === false && totalRowCount > 0

export const calculateUsedClassesCount = (
  library: Library,
  applicationID: string
) => {
  /* Usage count for "master" merged application.
   * his is only to match the figures calculated in other places. We'll need
   * a better way of calculating the total used classes for a merged app:
   * the total number of all unique classes used by all the individual apps
   * within the merged one. This should also apply to "Classes Used" value on
   * the "Overview" tab:
   * src/sca/libraries/components/drilldown-panel/summary-bar/index.tsx
   * and to the "Usage" column in the app library grid:
   * src/sca/libraries/components/cell-content/usage-cell.tsx
   */
  let usageCount =
    library.mergedApplications &&
    library.mergedApplications.length > 0 &&
    library.applications &&
    library.applications.some((app) => app.appId === applicationID)
      ? library.usage.totalUsed
      : undefined

  /* Usage count for a single application if such data exists in libraryClassUsageCounts */
  usageCount =
    usageCount === undefined &&
    library.libraryClassUsageCounts &&
    library.libraryClassUsageCounts.some(
      (appUsage) => appUsage.appId === applicationID
    )
      ? getApplicationLibraryClassUsageCount({
          applicationID,
          library: {
            libraryClassUsageCounts: library.libraryClassUsageCounts
          }
        })
      : usageCount

  /* If nothing else works, default to library.usage.totalUsed, provided that
   * the application still matches of what we report the usage for
   */
  usageCount =
    usageCount === undefined &&
    library.applications &&
    library.applications.some((app) => app.appId === applicationID)
      ? library.usage.totalUsed
      : usageCount

  return usageCount === undefined ? 0 : usageCount
}

export function LibraryUsageGrid({
  language,
  libraryHash,
  library,
  applicationID
}: {
  language: LibraryAgentLanguage
  libraryHash: string
  library: Library
  applicationID: string
}) {
  const { formatMessage } = useIntl()
  const tableRef = React.useRef<HTMLDivElement | null>(null)
  const [pagination, setPagination] = React.useState<
    SkeletorTableState['pagination']
  >({
    pageIndex: 0,
    pageSize: 25
  })
  const {
    searchText,
    setSearchText,
    setHasData,
    setSortingOrder,
    sortingOrder
  } = useLibraryUsageGridContext()

  const data = useLibraryUsage({
    enabled: true,
    libraryHash,
    limit: pagination.pageSize,
    offset: pagination.pageIndex * pagination.pageSize,
    sortingOrder,
    searchText
  })

  const showEmptyState =
    searchText === undefined &&
    !data.isLibraryUsageLoading &&
    (!data.libraryUsage || data.libraryUsage?.observations.length === 0)

  React.useEffect(() => {
    /* Show the empty state instead of the grid if there are no observations returned from the API on initial panel load */
    setHasData(!showEmptyState)
  }, [setHasData, showEmptyState])

  if (showEmptyState) {
    return <LibraryUsageGridEmptyState />
  }

  const usedClassesCount = calculateUsedClassesCount(library, applicationID)

  return (
    <Stack flexDirection="column">
      <Stack>
        <SearchInput
          testId="library-usage-search-input"
          onChange={(_, value) => setSearchText(value || '')}
          value={searchText}
        />
      </Stack>
      <Stack flexDirection="column">
        <Table<ObservationType>
          ref={tableRef}
          data={data.libraryUsage?.observations ?? []}
          isLoading={data.isLibraryUsageLoading}
          tableOptions={{
            enableSorting: true,
            manualPagination: true,
            onSortingChange: setSortingOrder,
            onPaginationChange: setPagination,
            pageCount: Math.ceil(
              data.libraryUsage?.total && pagination.pageSize
                ? data.libraryUsage?.total / pagination.pageSize
                : 0
            ),
            state: { sorting: sortingOrder, pagination }
          }}
          PaginationProps={{
            totalResults: data.libraryUsage?.total,
            showInputNav: true,
            allowFirstPageNav: true
          }}
          columns={getColumns({
            library,
            language,
            tableWidth: tableRef.current?.clientWidth ?? 1500,
            usedClassesCount
          })}
          emptyStateProps={{
            headerText: null,
            bodyText: (
              <Text font="body" color="neutral70">
                {formatMessage({
                  id: 'title_libraryUsage_noSearchResults'
                })}
              </Text>
            )
          }}
          testId="library-usage-grid-cell"
        />
      </Stack>
    </Stack>
  )
}
